import React, { useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { roomById, ROOMS, VIEW_TABS } from '../data/rooms';
import { useSpace } from '../data/firestore';
import Moodboards from '../components/Moodboards';
import ViewPanel from '../components/ViewPanel';
import { useAuth } from '../AuthContext';

// One room's page: its bathroom section first, then the bedroom (layout rule
// from the owner). Each section has its own tab bar of technical views
// (render / blueprints / dimensions / studs / electrical / plumbing) plus the
// space's moodboards. Deep-link a view with ?view=electrical.

const STATUS_LABEL = { planning: 'Planning', 'in-progress': 'In progress', done: 'Done' };

function RoomSection({ section, defaultView }) {
  const { user } = useAuth();
  const canEdit = !!user;
  const { doc: space } = useSpace(section.spaceId);
  const [tab, setTab] = useState(
    VIEW_TABS.some((t) => t.id === defaultView) ? defaultView : VIEW_TABS[0].id
  );

  if (space === undefined) return <p className="cs-help">Loading…</p>;
  if (space === null) {
    return <div className="cs-empty">Space “{section.spaceId}” not found — seed the spaces from the dashboard.</div>;
  }

  return (
    <section className="cs-card" style={{ padding: 18, marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>{space.icon} {section.label}</h2>
        <span className="ah-status" style={{ fontSize: 12 }}>
          {STATUS_LABEL[space.status] || 'Planning'}
        </span>
        <span style={{ flex: 1 }} />
        <Link to={`/spaces/${space.id}`} className="cs-help" style={{ fontSize: 13 }}>
          files, lists & notes →
        </Link>
      </div>
      {space.description && <p className="cs-help" style={{ margin: '6px 0 0' }}>{space.description}</p>}

      <div className="ah-tabs" style={{ marginTop: 12 }}>
        {VIEW_TABS.map((t) => (
          <button
            key={t.id}
            className={`ah-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 14 }}>
        {tab === 'moodboards' ? (
          <Moodboards space={space} canEdit={canEdit} />
        ) : (
          <ViewPanel
            spaceId={space.id}
            viewId={tab}
            viewLabel={(VIEW_TABS.find((t) => t.id === tab) || {}).label || tab}
            canEdit={canEdit}
          />
        )}
      </div>
    </section>
  );
}

export default function RoomDetail() {
  const { id } = useParams();
  const room = roomById(id);
  const [searchParams] = useSearchParams();
  const defaultView = searchParams.get('view');

  if (!room) {
    return (
      <div className="cs-page">
        <p className="cs-help">Room not found. <Link to="/rooms">All rooms</Link></p>
      </div>
    );
  }

  const idx = ROOMS.findIndex((r) => r.id === room.id);
  const prev = ROOMS[(idx + ROOMS.length - 1) % ROOMS.length];
  const next = ROOMS[(idx + 1) % ROOMS.length];

  return (
    <div className="cs-page">
      <p style={{ margin: 0 }}><Link to="/rooms" className="cs-help">← Rooms</Link></p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
        <h1 style={{ margin: 0 }}>{room.icon} {room.name}</h1>
        <span style={{ flex: 1 }} />
        <Link to={`/rooms/${prev.id}`} className="cs-help" style={{ fontSize: 13 }}>← {prev.name}</Link>
        <Link to={`/rooms/${next.id}`} className="cs-help" style={{ fontSize: 13 }}>{next.name} →</Link>
      </div>
      <p className="cs-help" style={{ marginTop: 4 }}>{room.blurb}</p>

      {room.sections.map((s) => (
        <RoomSection key={s.spaceId} section={s} defaultView={defaultView} />
      ))}
    </div>
  );
}
